import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Home } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goToGoogle() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthGate({ open, onOpenChange, contextInfo }) {
  const { login, register } = useAuth();
  const { t } = useLocale();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", phone: "", role: "buscador" });

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      // Modal closes automatically via AuthGateProvider effect
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...regForm, phone: regForm.phone.trim() || null });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="auth-gate-modal">
        <div className="bg-primary text-primary-foreground px-6 pt-8 pb-6">
          <div className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-primary-foreground/10 mb-3">
            <Home className="h-5 w-5" />
          </div>
          <DialogHeader className="p-0 space-y-1">
            <DialogTitle className="font-serif text-2xl text-primary-foreground">
              {contextInfo?.title || t("gate.title")}
            </DialogTitle>
            <DialogDescription className="text-primary-foreground/75 text-sm">
              {contextInfo?.sub || t("gate.sub")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-3">
          {/* Google */}
          <Button
            type="button"
            onClick={goToGoogle}
            variant="outline"
            className="w-full rounded-full h-11 justify-start pl-4"
            data-testid="gate-google"
          >
            <svg className="h-4 w-4 mr-3" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            <span className="flex-1 text-left">{t("gate.google")}</span>
          </Button>

          {/* Apple */}
          <Button
            type="button"
            onClick={() => toast.info(t("gate.apple_soon"))}
            variant="outline"
            className="w-full rounded-full h-11 justify-start pl-4 bg-black text-white hover:bg-black/90 hover:text-white border-black"
            data-testid="gate-apple"
          >
            <svg className="h-4 w-4 mr-3" viewBox="0 0 384 512" fill="currentColor">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            <span className="flex-1 text-left">{t("gate.apple")}</span>
            <span className="text-[10px] font-normal opacity-70 uppercase tracking-widest">{t("gate.soon")}</span>
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("gate.or_email")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="gate-tab-login">{t("auth.submit_login")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="gate-tab-register">{t("auth.submit_register")}</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="pt-4">
              <form onSubmit={doLogin} className="space-y-3" data-testid="gate-login-form">
                <Input type="email" required placeholder={t("auth.email")} value={loginForm.email}
                       onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                       data-testid="gate-login-email" />
                <Input type="password" required placeholder={t("auth.password")} value={loginForm.password}
                       onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                       data-testid="gate-login-password" />
                <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="gate-login-submit">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("auth.submit_login")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="pt-4">
              <form onSubmit={doRegister} className="space-y-3" data-testid="gate-register-form">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "buscador", label: t("auth.role_buscador") },
                    { v: "agente", label: t("auth.role_agente") },
                  ].map((r) => (
                    <button
                      key={r.v}
                      type="button"
                      onClick={() => setRegForm({ ...regForm, role: r.v })}
                      data-testid={`gate-role-${r.v}`}
                      className={`px-3 py-2 border text-xs uppercase tracking-widest rounded-sm transition-colors ${
                        regForm.role === r.v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <Input required placeholder={t("auth.name")} value={regForm.name}
                       onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                       data-testid="gate-reg-name" />
                <Input type="email" required placeholder={t("auth.email")} value={regForm.email}
                       onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                       data-testid="gate-reg-email" />
                <Input type="password" required minLength={6} placeholder={t("auth.password")} value={regForm.password}
                       onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                       data-testid="gate-reg-password" />
                <Input type="tel" inputMode="tel" placeholder="+1 809 555 0100" value={regForm.phone}
                       onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                       data-testid="gate-reg-phone" />
                <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="gate-reg-submit">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("auth.submit_register")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-[11px] text-muted-foreground text-center pt-2 leading-relaxed">
            {t("gate.legal")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
